import { useCallback, useEffect, useState } from 'react';
import { Check, ChevronsUpDown, Loader2, Search } from 'lucide-react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from '../../ui/command';
import { cn } from '../../../lib/utils';
import { AdminTenant, apiService } from '../../../services/api';
import { DIRECT_CUSTOMERS_SELECTION, type TenantSelection } from '../../../contexts/tenantSelection';

/** Wartezeit vor der Suchanfrage, damit Tippen keine Anfrage je Anschlag auslöst. */
const SEARCH_DEBOUNCE_MS = 300;

/** Tenants pro Trefferliste — reicht für eine Auswahl per Suche, ohne alles zu laden. */
const SEARCH_LIMIT = 20;

/** Fester Zusatz-Eintrag am Listenanfang (z.B. "<unzugeordnet>") — kein "Alle Tenants". */
export interface TenantPickerExtraOption {
  id: string;
  label: string;
  onSelect: () => void;
}

export interface TenantPickerProps {
  /** Tenant-ID der aktuellen Auswahl; null = noch keine Auswahl getroffen. */
  selectedTenantId: string | null;
  /**
   * Anzeigename der aktuellen Auswahl, falls (noch) kein passendes Tenant-Objekt
   * in der geladenen Trefferliste steckt — z.B. Default aus dem globalen Header-Filter.
   */
  selectedTenantName?: string | null;
  /** Gewählter Tenant — echter Tenant aus der Suche oder der feste Default-Tenant-Eintrag. */
  onSelect: (tenant: TenantSelection) => void;
  /** Fester Zusatz-Eintrag am Listenanfang (kursiv/grau), z.B. "<unzugeordnet>" im Quell-Filter. */
  extraOption?: TenantPickerExtraOption;
  /** Tenant-ID, die in der Liste ausgegraut/nicht wählbar ist (z.B. der aktuell gewählte Quell-Tenant im Ziel-Picker). */
  excludeTenantId?: string | null;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  /** Für `aria-label`/Tests eindeutige Kennzeichnung, wenn mehrere Picker auf einer Seite sitzen. */
  triggerAriaLabel?: string;
}

/**
 * Tenant-Auswahl mit serverseitiger Suche über `getAdminTenants` (analog
 * {@link SeverityCustomerPicker}). Bietet nie "Alle Tenants" — es wird immer
 * genau ein Tenant (oder der optionale `extraOption`) gewählt. Der Default-Tenant
 * steckt nicht in `getAdminTenants` und steht daher (wie im Header) fest oben.
 * Pausierte Tenants bleiben wählbar und tragen die Badge "Pausiert".
 */
export function TenantPicker({
  selectedTenantId,
  selectedTenantName,
  onSelect,
  extraOption,
  excludeTenantId,
  disabled = false,
  placeholder = 'Tenant suchen…',
  className,
  triggerAriaLabel,
}: TenantPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [tenants, setTenants] = useState<AdminTenant[]>([]);
  const [loading, setLoading] = useState(false);

  const loadTenants = useCallback(async (term: string) => {
    try {
      setLoading(true);
      const response = await apiService.getAdminTenants({ search: term.trim() || undefined, limit: SEARCH_LIMIT });
      setTenants(response.data);
    } catch {
      // Fehlerfall bleibt still: die Liste zeigt dann „Kein Tenant gefunden".
      setTenants([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => void loadTenants(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [open, search, loadTenants]);

  const selectedFromList = tenants.find((tenant) => tenant.id === selectedTenantId);
  const label =
    extraOption && selectedTenantId === extraOption.id
      ? extraOption.label
      : selectedTenantId === DIRECT_CUSTOMERS_SELECTION.id
        ? DIRECT_CUSTOMERS_SELECTION.name
        : (selectedFromList?.name ?? selectedTenantName ?? null);
  const isDefaultExcluded = excludeTenantId === DIRECT_CUSTOMERS_SELECTION.id;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={triggerAriaLabel}
          disabled={disabled}
          className={cn('w-[280px] justify-between font-normal', className)}
        >
          <span className="flex min-w-0 items-center gap-2">
            <Search className="h-4 w-4 flex-none text-gray-400" />
            <span className={cn('truncate', !label && 'text-gray-400')}>{label ?? placeholder}</span>
          </span>
          <ChevronsUpDown className="h-4 w-4 flex-none opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[320px] p-0">
        <Command shouldFilter={false}>
          <CommandInput placeholder={placeholder} value={search} onValueChange={setSearch} />
          <CommandList>
            {loading && (
              <div className="flex items-center justify-center gap-2 py-6 text-sm text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                Tenants werden geladen…
              </div>
            )}
            {!loading && extraOption && (
              <CommandItem
                value={extraOption.id}
                onSelect={() => {
                  extraOption.onSelect();
                  setOpen(false);
                }}
              >
                <Check
                  className={cn('mr-2 h-4 w-4', selectedTenantId === extraOption.id ? 'opacity-100' : 'opacity-0')}
                />
                <span className="italic text-gray-500">{extraOption.label}</span>
              </CommandItem>
            )}
            {!loading && (
              <CommandItem
                value={DIRECT_CUSTOMERS_SELECTION.id}
                disabled={isDefaultExcluded}
                onSelect={() => {
                  if (isDefaultExcluded) return;
                  onSelect(DIRECT_CUSTOMERS_SELECTION);
                  setOpen(false);
                }}
              >
                <Check
                  className={cn(
                    'mr-2 h-4 w-4',
                    selectedTenantId === DIRECT_CUSTOMERS_SELECTION.id ? 'opacity-100' : 'opacity-0',
                  )}
                />
                <span className="min-w-0 flex-1 truncate">{DIRECT_CUSTOMERS_SELECTION.name}</span>
                <span className="ml-2 shrink-0 text-xs text-gray-400">Callbert</span>
              </CommandItem>
            )}
            {!loading && tenants.length === 0 && <CommandEmpty>Kein Tenant gefunden.</CommandEmpty>}
            {!loading &&
              tenants.map((tenant) => {
                const isExcluded = tenant.id === excludeTenantId;
                return (
                  <CommandItem
                    key={tenant.id}
                    value={tenant.id}
                    disabled={isExcluded}
                    onSelect={() => {
                      if (isExcluded) return;
                      onSelect({ id: tenant.id, name: tenant.name });
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn('mr-2 h-4 w-4', selectedTenantId === tenant.id ? 'opacity-100' : 'opacity-0')}
                    />
                    <span className="min-w-0 flex-1 truncate">{tenant.name}</span>
                    {!tenant.isActive && (
                      <Badge variant="secondary" className="ml-2 shrink-0">
                        Pausiert
                      </Badge>
                    )}
                  </CommandItem>
                );
              })}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
